import type { ScaleLinear, ScaleLogarithmic, ScaleSymLog } from 'd3-scale';

import type { ChartUnit } from '../../core/types/chart-unit.js';
import type { ColorToken } from '../../core/types/color-palette.js';

/**
 * @public
 */
export type HistogramZoomDomain = number[];

/**
 * @public
 */
export interface HistogramBin {
  /**
   * The start of the bin or bucket
   */
  from: number;
  /**
   * The end of the bin or bucket
   */
  to: number;
  /**
   * The value, better known as "frequency"
   */
  value: number;
}

/**
 * @public
 */
export interface HistogramSeries extends Record<string, unknown> {
  /**
   * The histogram series data to be graphed by the component
   */
  bins: HistogramBin[];
  /**
   * The name for the histogram series
   */
  name: string;
  /**
   * Unit token for the values in the y-axis of the histogram series
   */
  unit?: ChartUnit;
  /**
   * The color to be applied to the series
   */
  color?: string;
}

/**
 * @internal
 */
export interface SeriesMetadata {
  id: string;
  color: ColorToken;
  scaleIndex: number;
}

/**
 * @internal
 */
export interface TooltipValueMetadata {
  value: number;
  unit: ChartUnit;
}

/**
 * @internal
 */
export interface BinMetadata extends HistogramBin {
  seriesId: string;
  color: ColorToken;
  unit: ChartUnit;
  seriesName: string;
  cumulativeValue?: number;
  absolute?: TooltipValueMetadata;
}

/**
 * @internal
 */
export type WeakMapSeries = WeakMap<object, SeriesMetadata>;
/**
 * @internal
 */
export type WeakMapBins = WeakMap<object, BinMetadata>;

/**
 * @internal
 */
export interface HistogramDataPoint {
  /**
   * The value in X axis
   */
  x: number;
  /**
   * The value in Y axis
   */
  y: number;
}

/**
 * @public
 */
export const histogramTooltipVariantOptions = [
  'single',
  'shared',
  'grouped',
] as const;
/**
 * @public
 */
export type HistogramTooltipVariant =
  (typeof histogramTooltipVariantOptions)[number];

/**
 * @internal
 */
export type XScale =
  | ScaleLinear<number, number>
  | ScaleLogarithmic<number, number>
  | ScaleSymLog<number, number>;
/**
 * @internal
 */
export type YScale =
  | ScaleLinear<number, number>
  | ScaleLogarithmic<number, number>
  | ScaleSymLog<number, number>;
